import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import Section from '@/components/Section'
import { usePageTitle } from '@/components/usePageTitle'
import { fulfillCheckout, getCheckout, type CheckoutSession } from '@/lib/api'
import { hexContrast } from '@/lib/color'
import { formatPrice } from '@/lib/register'

/**
 * Mock checkout. Stands in for a hosted Stripe Checkout page: shows the order and a Pay button, takes no card, and
 * completes the registration through /api/fulfill.
 */
export default function Checkout() {
  usePageTitle('Checkout', 'Complete your ColorMark registration.')
  const [params] = useSearchParams()
  const sessionId = params.get('session_id') ?? ''
  const navigate = useNavigate()
  const [session, setSession] = useState<CheckoutSession | null | 'loading'>('loading')
  const [paying, setPaying] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    ;(sessionId ? getCheckout(sessionId) : Promise.resolve(null)).then((s) => {
      if (alive) setSession(s)
    })
    return () => {
      alive = false
    }
  }, [sessionId])

  if (session === 'loading') return <Section className="min-h-svh pt-40 md:pt-52" />
  if (!session) {
    return (
      <Section className="min-h-svh pt-40 md:pt-52">
        <div className="mx-auto max-w-[1800px]">
          <h1>Checkout not found.</h1>
          <p className="mt-8">
            That checkout session has expired or never existed. <Link to="/register">Start a new registration.</Link>
          </p>
        </div>
      </Section>
    )
  }
  if (session.status === 'paid') return <Navigate to={`/register/success?session_id=${encodeURIComponent(session.id)}`} replace />

  async function pay() {
    if (!session || session === 'loading') return
    setPaying(true)
    setFailure(null)
    try {
      await fulfillCheckout(session.id)
      navigate(`/register/success?session_id=${encodeURIComponent(session.id)}`, { replace: true })
    } catch (err) {
      setPaying(false)
      setFailure(err instanceof Error && err.message && !err.message.startsWith('fulfill:') ? err.message : 'Sorry, that did not go through. Please try again.')
    }
  }

  const fg = hexContrast(session.hex) === 'black' ? '#111111' : '#ffffff'

  return (
    <Section className="min-h-svh pt-40 md:pt-52">
      <div className="mx-auto max-w-[1800px]">
        <h1>Checkout.</h1>
        <div className="mt-10 grid max-w-5xl grid-cols-1 gap-10 font-body lg:grid-cols-2">
          <div className="border border-neutral-300">
            <div className="flex items-center gap-4 px-6 py-6 text-xl" style={{ backgroundColor: session.hex, color: fg }}>
              <span className="font-medium tracking-wide">{session.hex}</span>
              <span>{session.name}</span>
            </div>
            <dl className="space-y-4 px-6 py-6">
              <div className="flex justify-between gap-6">
                <dt>ColorMark Registration</dt>
                <dd className="text-right">{session.plan.label}</dd>
              </div>
              <div className="flex justify-between gap-6 border-t border-neutral-200 pt-4 text-lg font-medium">
                <dt>Total</dt>
                <dd>{formatPrice(session.amountCents)}</dd>
              </div>
            </dl>
          </div>

          <div>
            <p className="inline-block rounded bg-amber-100 px-3 py-1 text-sm font-medium text-amber-900">Test mode</p>
            <p className="mt-4">
              Payments are not connected yet. This test checkout completes the registration of{' '}
              <strong>{session.name}</strong> for {session.firstName} without charging a card.
            </p>
            <button type="button" onClick={pay} disabled={paying} className="btn btn-black mt-8 w-full sm:w-auto">
              {paying ? 'Completing…' : `Pay ${formatPrice(session.amountCents)}`}
            </button>
            <p className="mt-4 min-h-6 text-sm text-red-700" role="alert">
              {failure}
            </p>
            <p className="mt-6 text-sm">
              <Link to={`/register?hex=${session.hex.slice(1)}`}>Cancel and go back</Link>
            </p>
          </div>
        </div>
      </div>
    </Section>
  )
}
